import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { request } from '../../sync/api.js';

/**
 * Во сколько зовут кандидатов и сколько человек берут на одно время.
 *
 * Времена задаются здесь, а не зашиты в код: у производства они меняются
 * вместе с загрузкой цеха, и ждать выпуска новой версии ради «на полчаса
 * раньше» неправильно.
 */

interface Slots {
  slots: string[];
  capacity: number;
}

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

export function InterviewSlotsScreen(): JSX.Element {
  const [slots, setSlots] = useState<string[]>([]);
  /**
   * Вместимость держим текстом, а не числом.
   *
   * Числовое состояние означало бы, что пустое поле надо чем-то заменить —
   * и на месте стёртой цифры тут же появлялась единица, поверх которой уже
   * не напечатать. Поле должно позволять быть пустым, пока его правят;
   * числом оно становится при сохранении.
   */
  const [capacity, setCapacity] = useState('6');
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const response = await request<Slots>('/api/hr/slots', { method: 'GET' }, 20_000);
      setSlots(response.slots);
      setCapacity(String(response.capacity));
      setError(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось загрузить слоты');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = (): void => {
    const value = draft.trim();
    if (!TIME_PATTERN.test(value)) {
      setError('Время в формате ЧЧ:ММ, например 09:30');
      return;
    }
    if (slots.includes(value)) {
      setError('Такое время уже есть');
      return;
    }
    setError(null);
    setSlots([...slots, value].sort());
    setDraft('');
  };

  const save = async (): Promise<void> => {
    const parsed = Number.parseInt(capacity, 10);
    if (!Number.isInteger(parsed) || parsed < 1) {
      setError('Сколько человек на одно время — целое число, не меньше одного');
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await request<Slots>(
        '/api/hr/slots',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slots, capacity: parsed }),
        },
        20_000,
      );
      setSlots(saved.slots);
      setCapacity(String(saved.capacity));
      setNotice('Сохранено. HR увидит новые времена сразу');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen px-4 py-6">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Слоты собеседований</h1>
        <Link to="/" className="rounded-xl bg-muted px-4 py-3 text-base font-semibold">
          Назад
        </Link>
      </header>

      {error && <p className="mb-4 rounded-xl bg-rose-50 text-rose-800 ring-1 ring-rose-200 px-4 py-3 text-base">{error}</p>}
      {notice && (
        <p className="mb-4 rounded-xl bg-muted px-4 py-3 text-base text-body">{notice}</p>
      )}

      <section className="mb-6 rounded-xl bg-card shadow-card p-4">
        <h2 className="mb-3 text-lg font-semibold">Во сколько зовём</h2>

        <div className="mb-4 flex flex-wrap gap-2">
          {slots.map((slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => setSlots(slots.filter((item) => item !== slot))}
              className="rounded-xl bg-muted px-5 py-3 text-lg font-semibold tabular-nums active:bg-line"
            >
              {slot} ×
            </button>
          ))}
          {slots.length === 0 && (
            <p className="text-base text-amber-700">
              Ни одного времени — записать кандидата будет некуда.
            </p>
          )}
        </div>
        <p className="mb-4 text-base text-dim">Нажмите на время, чтобы убрать его.</p>

        <div className="flex gap-2">
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="09:30"
            inputMode="numeric"
            className="flex-1 rounded-xl bg-muted px-4 py-3 text-base tabular-nums"
          />
          <button
            type="button"
            onClick={add}
            className="rounded-xl bg-muted px-5 py-3 text-base font-semibold"
          >
            Добавить
          </button>
        </div>
      </section>

      <section className="mb-6 rounded-xl bg-card shadow-card p-4">
        <h2 className="mb-2 text-lg font-semibold">Сколько человек на одно время</h2>
        <p className="mb-3 text-base text-dim">
          Больше этого числа на слот не запишут — HR увидит отказ во время звонка.
        </p>
        <input
          value={capacity}
          // Пустое поле разрешено: человек стирает старое значение, чтобы
          // набрать новое, и подставлять что-то за него на это время нельзя
          onChange={(event) => setCapacity(event.target.value.replace(/\D/g, '').slice(0, 3))}
          onFocus={(event) => event.target.select()}
          inputMode="numeric"
          className="w-32 rounded-xl bg-muted px-4 py-3 text-lg tabular-nums"
        />
      </section>

      <button
        type="button"
        disabled={busy || slots.length === 0 || capacity === ''}
        onClick={() => void save()}
        className="w-full rounded-xl bg-sky-700 px-5 py-4 text-lg font-semibold disabled:opacity-50"
      >
        {busy ? 'Сохраняю…' : 'Сохранить'}
      </button>
    </div>
  );
}
